/**
 * Room ma'lumot qatlami — docs/API.md §8.
 *
 * Holat IKKI manbadan keladi va bu ataylab:
 *
 *   `GET /rooms/{id}`   kirishda bir marta — to'liq snapshot
 *   WS `room.*`         keyin — delta
 *
 * Faqat WS bo'lsa qayta ulanishda klient holati serverdan ajralib
 * ketardi. Faqat so'rov bo'lsa ikkinchi odam kirganini birinchisi
 * ko'rmasdi.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { request } from './client';

export interface RoomSeat {
  idx: number;
  user_id: number;
  first_name: string;
  palette_idx: number;
  is_muted: boolean;
}

export interface RoomState {
  id: number;
  title: string;
  topic: string;
  type: string;
  capacity: number;
  is_live: boolean;
  host_id: number;
  started_at: string;
  seats: RoomSeat[];
  listeners: number;
  my_seat: number | null;
}

export interface ChatMessage {
  id: number;
  kind: string;
  user_id: number | null;
  first_name: string | null;
  palette_idx: number | null;
  text: string | null;
  created_at: string;
}

export const roomKey = (id: number) => ['room', id] as const;
export const roomChatKey = (id: number) => ['room', id, 'chat'] as const;

export function useRoom(roomId: number, enabled = true) {
  return useQuery({
    queryKey: roomKey(roomId),
    queryFn: () => request<RoomState>(`/rooms/${roomId}`),
    // ⚠️ `enabled` — KIRISHDAN keyin. Server har room yo'lida
    // a'zolikni tekshiradi (D-118) va kirishdan oldin so'rov 403
    // oladi hamda keshga xato holat tushadi.
    enabled,
    // Yangilanish WS'dan keladi — davriy so'rov ortiqcha yuk bo'lardi.
    staleTime: Infinity,
    retry: false,
  });
}

export function useRoomChat(roomId: number, enabled = true) {
  return useQuery({
    queryKey: roomChatKey(roomId),
    queryFn: () => request<{ items: ChatMessage[] }>(`/rooms/${roomId}/messages?limit=50`),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

export interface RoomRow {
  id: number;
  title: string;
  topic: string;
  type: string;
  capacity: number;
  host_id: number;
  host_name: string;
  listeners: number;
  speakers: number;
  started_at: string;
}

/** TOP — `score` bo'yicha. `filter=new` — yangi roomlar (D-006). */
export function useRoomList(filter: 'top' | 'new', enabled = true) {
  return useQuery({
    queryKey: ['rooms', filter] as const,
    queryFn: () => request<{ items: RoomRow[] }>(`/rooms?filter=${filter}&limit=20`),
    // `rooms_enabled` o'chiq bo'lsa so'rov YUBORILMAYDI — u 404 qaytaradi
    // va bosh sahifada xato paneli chiqardi (audit T-032).
    enabled,
    // Ro'yxat tez eskiradi — 30 soniyada score qayta hisoblanadi.
    staleTime: 20_000,
    retry: false,
  });
}

export interface TopEntry {
  rank: number;
  user_id: number;
  first_name: string;
  palette_idx: number;
  /** MA'NOSI o'qqa bog'liq: charm yoki efir daqiqalari. */
  value: number;
  is_me: boolean;
}

/** Ikki ALOHIDA jadval (D-003) — charm sotib olinadi, level yo'q. */
export function useLeaderboard(axis: 'charm' | 'level', period: 'today' | 'week' | 'month') {
  return useQuery({
    queryKey: ['top', axis, period] as const,
    queryFn: () =>
      request<{ items: TopEntry[]; me: TopEntry | null }>(
        `/rooms/top?axis=${axis}&period=${period}&limit=10`,
      ),
    staleTime: 60_000,
    retry: false,
  });
}

export interface TopSponsor {
  user_id: number;
  first_name: string;
  palette_idx: number;
  /** Homiylik OCHIQ narsa — yuboruvchi aynan ko'rinish uchun to'laydi
   *  (qoida 2). Qabul qiluvchining charmi bu yerda ko'rsatilmaydi. */
  total_gems: number;
  gifts: number;
}

/**
 * Room'ning BUGUNGI top homiylari.
 *
 * `staleTime` qisqa: sovg'a yuborilgandan keyin qator tez yangilanishi
 * kerak — yuboruvchi aynan shu ko'rinish uchun to'lagan.
 */
export function useTopSponsors(roomId: number, enabled = true) {
  return useQuery({
    queryKey: ['rooms', roomId, 'sponsors'] as const,
    queryFn: () =>
      request<{ room_id: number; items: TopSponsor[] }>(
        `/gifts/top-sponsors?room_id=${roomId}&limit=3`,
      ),
    enabled,
    staleTime: 15_000,
    retry: false,
  });
}

/** Hafta tanlovi — haftalik ball bo'yicha. `null` bo'lsa karta yo'q. */
export function useFeaturedRoom(enabled = true) {
  return useQuery({
    queryKey: ['rooms', 'featured'] as const,
    queryFn: () => request<RoomRow | null>('/rooms/featured'),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

interface CreateRoomBody {
  title: string;
  topic: string;
  type: 'open' | 'password';
  capacity: number;
  password?: string;
}

export function useCreateRoom() {
  return useMutation({
    mutationFn: (body: CreateRoomBody) => request<RoomState>('/rooms', { method: 'POST', body }),
  });
}

export function useJoinRoom(roomId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (password?: string) =>
      request<RoomState>(`/rooms/${roomId}/join`, {
        method: 'POST',
        body: password ? { password } : {},
      }),
    onSuccess: (state) => qc.setQueryData(roomKey(roomId), state),
  });
}

export function useClaimSeat(roomId: number) {
  return useMutation({
    mutationFn: (idx: number) =>
      request<{ idx: number; can_publish: boolean }>(`/rooms/${roomId}/seats/${idx}`, {
        method: 'POST',
      }),
  });
}

export function useReleaseSeat(roomId: number) {
  return useMutation({
    mutationFn: (idx: number) =>
      request<{ ok: boolean }>(`/rooms/${roomId}/seats/${idx}`, { method: 'DELETE' }),
  });
}

export function useMicRequest(roomId: number) {
  return useMutation({
    mutationFn: () =>
      request<{ sent: boolean }>(`/rooms/${roomId}/mic-request`, { method: 'POST' }),
  });
}

export function useSendRoomMessage(roomId: number) {
  return useMutation({
    mutationFn: (text: string) =>
      request<ChatMessage>(`/rooms/${roomId}/messages`, { method: 'POST', body: { text } }),
  });
}

export function useLeaveRoom(roomId: number) {
  return useMutation({
    mutationFn: () => request<{ ok: boolean }>(`/rooms/${roomId}/leave`, { method: 'POST' }),
  });
}
